import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Divider, Grid, Heading, Link, Skeleton, Text, useIsBreakpointMediumAndUp, useModalDeepLink } from '@afterpaytouch/core'
import styles from './style.module.css'
import { useCardBillingAddressModal, useOneCardModal } from './PaymentMethods.hooks'
import { useConsumerSizes } from '../../../utils/responsive'
import { Trans, useTranslation } from 'next-i18next'
import clsx, { ClassValue } from 'clsx'
import { AchBankAccountStatuses, BankAccountDetails, CardBrand, CardExpiryStatus, CashAppPayDetail, isBankAccount, isCard, isCashAppPay } from '@afterpay/types'
import {
  useApplePay,
  useBankAccountsHasResults,
  useConsumerCountry,
  useConsumerPreferredPaymentMethod,
  useCreditCardsHasResults,
  useGetCreditCardsQuery,
} from '../../../state'
import { PaymentMethodRow } from '../../PaymentMethodRow'
import { PaymentMenu } from './PaymentMenu'
import { AddCardModal } from './AddCardModal'
import { ErrorDialog } from '../../ErrorDialog'
import { CreditCard } from '@afterpaytouch/portal-api/consumer/account/creditcards/types'
import { stringToBoolean } from '@afterpay/utils/string'
import ActionModal from './ActionModal'
import { createModalHook, useFlag, useSearchParam } from '../../../hooks'
import ApplePayLearnMoreModal from './ApplePayLearnMoreModal'
import { useOnlyActiveSubscriptionOrders, usePaymentMethodsBusinessLogic } from './hooks'
import { maskCreditCardNumber } from '../../../utils/card'
import { AchFlowModal } from '../../Ach'
import { PaymentMethodType, PreferredPaymentMethod } from '@afterpaytouch/portal-api'
import { ReportCardCancellationModal, useReportCardCancellationModal } from '../../ReportCardCancellationModal'
import { useCashAppPaysHasResults, useGetCashAppPaysQuery } from '../../../state/cashAppPays/hooks'
import { BillingAddressModal } from './BillingAddressModal'
import { RelinkModal } from './RelinkModal'
import { Route } from '../../../config/router'
import { AchFlowSteps } from '../../Ach/AchFlowModalContext'
import { useACHModal } from '../../Ach/hooks'
import { useShouldBlockChaseCard } from '../../PayNowModal/Payment/hooks'
import { isACHEnabledCountry } from '../../../model'

const I18N_NAMESPACE = ['common', 'account']

export const PaymentMethods: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { subheadingSize, textSize } = useConsumerSizes()
  const buttonContainerStyle: ClassValue = clsx(styles.my8, styles.mt16)
  const {
    data: cardData,
    isLoading: isLoadingCardData,
    isFetching: isFetchingCardData,
    isError: isLoadingCardError,
    refetch: refetchCard,
  } = useGetCreditCardsQuery()
  const {
    data: cashData,
    isLoading: isLoadingCashData,
    isFetching: isFetchingCashData,
    isError: isLoadingCashError,
    refetch: refetchCash,
  } = useGetCashAppPaysQuery()
  const hasCreditCardData: boolean = useCreditCardsHasResults()
  const hasBankAccountData: boolean = useBankAccountsHasResults()
  const hasCashAppPayData: boolean = useCashAppPaysHasResults()
  const isDesktop: boolean = useIsBreakpointMediumAndUp() ?? false
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<CreditCard | BankAccountDetails | CashAppPayDetail | PaymentMethodType.APPLE_PAY | null>(
    null
  )
  const [includingAgreements, setIncludingAgreements] = useState<boolean>(true)
  const [numOfCards, setNumOfCards] = useState<number>(0)
  const {
    handleRemoveCard,
    handlePreferredCard,
    handlePreferredApplePay,
    handlePreferredBank,
    handleRemoveBank,
    handlePreferredCashAppPay,
    handleRemoveCashAppPay,
  } = usePaymentMethodsBusinessLogic(selectedPaymentMethod, includingAgreements)
  const { isApplePayAvailable } = useApplePay()
  const consumerPaymentMethod: PreferredPaymentMethod | PaymentMethodType = useConsumerPreferredPaymentMethod() ?? PreferredPaymentMethod.CREDIT_CARD
  const isApplePayPreferred = consumerPaymentMethod === PreferredPaymentMethod.APPLE_PAY
  const isAmexSoftCloseEnabled = useFlag('amex-soft-close-enabled', false)
  const isApplePayDisabled: boolean = useFlag('consumer-portal-is-apple-pay-disabled', false)
  const country = useConsumerCountry()
  const isUSConsumer = country === 'US'
  const isAchFlagEnabled = isACHEnabledCountry(country)
  const { isChaseCreditCardBlocked } = useShouldBlockChaseCard()

  const [isAchEnabled, setIsAchEnabled] = useState(false)

  useEffect(() => {
    setIsAchEnabled(isAchFlagEnabled && hasCreditCardData)
  }, [hasCreditCardData, isAchFlagEnabled])

  useEffect(() => {
    if (!isLoadingCardData) {
      setNumOfCards(cardData?.creditcards?.length ?? 0)
    }
  }, [isLoadingCardData, setNumOfCards, cardData])

  /**
   * ACH flow modal
   * parse query params from ach modal deep link to set initial state of ACH flow modal
   */
  const {
    achFlowInitialStep,
    setAchFlowInitiateStep,
    isAchFlowModalOpen,
    openAchFlowModal,
    closeAchFlowModal,
    bankData,
    isLoadingBankData,
    isFetchingBankData,
    isBankError,
    refetchBank,
    setIsAchSuccess,
  } = useACHModal()

  const handleDataRetrievalError = useCallback(() => {
    if (isBankError) {
      refetchBank()
    }
    if (isLoadingCardError) {
      refetchCard()
    }
    if (isLoadingCashError) {
      refetchCash()
    }
  }, [isBankError, isLoadingCardError, isLoadingCashError, refetchBank, refetchCard, refetchCash])
  const [existingOrderContent, setExistingOrderContent] = useState('')
  const hasOnlyActiveSubscriptions = useOnlyActiveSubscriptionOrders()
  /**
   * Remove payment method modal
   */
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false)

  const handleShowRemoveModal = (): void => {
    setShowRemoveModal(true)
  }

  const handleCloseRemoveModal = (): void => {
    setShowRemoveModal(false)
  }

  /**
   * Preferred payment method modal
   */
  const [showPreferredModal, setShowPreferredModal] = useState<boolean>(false)

  const handleShowPreferredModal = (): void => {
    setShowPreferredModal(true)
  }

  const handleClosePreferredModal = (): void => {
    setShowPreferredModal(false)
  }

  /**
   * Report card cancellation modal
   */
  const {
    reportCancellationCtaVisible: isReportCardCancellationCtaVisible,
    isModalOpen: isReportCardCancellationModalOpen,
    openModal: openReportCardCancellationModal,
    closeModal: closeReportCardCancellationModal,
  } = useReportCardCancellationModal()

  /**
   * Existing order remove modal
   */
  const [showExistingOrderModal, setShowExistingOrderModal] = useState<boolean>(false)

  const handleShowExistingOrderModal = (): void => {
    setShowExistingOrderModal(true)
  }

  const handleCloseExistingOrderModal = (): void => {
    setShowExistingOrderModal(false)
  }

  useEffect(() => {
    if (hasOnlyActiveSubscriptions) {
      setExistingOrderContent(t('account:tab:payment-methods:existingOrderModal:bodySubscriptions'))
    } else {
      setExistingOrderContent(t('account:tab:payment-methods:existingOrderModal:body'))
    }
  }, [hasOnlyActiveSubscriptions])

  /**
   * One card on account modal
   */
  const [isOneCardModalOpen, { openModal: openOneCardModal, closeModal: closeOneCardModal }] = useOneCardModal(false)

  const handleCloseModalOpenAddCard = (): void => {
    closeOneCardModal()
    openAddCardModal()
  }

  /**
   * Card billing address modal
   */
  const [isCardBillingAddressModalOpen, { openModal: openCardBillingAddressModal, closeModal: closeCardBillingAddressModal }] =
    useCardBillingAddressModal(false)

  /**
   * Add payment method modal
   */

  const apmModalId = 'apm'
  const apmModalSearchParam = stringToBoolean(useSearchParam(`modal-${apmModalId}`) ?? '')
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState<boolean>(apmModalSearchParam)
  const openAddCardModal = (): void => {
    setIsAddCardModalOpen(true)
  }
  const closeAddCardModal = (): void => {
    setIsAddCardModalOpen(false)
  }

  const openAchFlowModalPlaidStep = (bankAccount: BankAccountDetails): void => {
    setBankAccountToBeRelinked(bankAccount)
    setAchFlowInitiateStep(AchFlowSteps.RelinkPlaid)
    openAchFlowModal()
    setIsAchSuccess(false)
  }

  useModalDeepLink(isAddCardModalOpen, apmModalId)

  const [isRelinkModalOpen, { openModal: openRelinkModal, closeModal: closeRelinkModal }] = createModalHook()(false, {
    onClose: () => {
      setBankAccountToBeRelinked(undefined)
    },
  })

  const [bankAccountToBeRelinked, setBankAccountToBeRelinked] = useState<BankAccountDetails>()

  const clickRelinkItem = (bankAccount: BankAccountDetails): void => {
    openRelinkModal()
    setBankAccountToBeRelinked(bankAccount)
  }

  /**
   * Apple pay learn more modal
   */
  const apModalLearnMore = 'apLearnMore'
  const apModalLearnMoreSearchParam = stringToBoolean(useSearchParam(`modal-${apModalLearnMore}`) ?? '')
  const [isApplePayLearnMoreModalOpen, setIsApplePayLearnMoreModalOpen] = useState<boolean>(apModalLearnMoreSearchParam)
  const openApplePayLearnMoreModal = (): void => {
    setIsApplePayLearnMoreModalOpen(true)
  }
  const closeApplePayLearnMoreModal = (): void => {
    setIsApplePayLearnMoreModalOpen(false)
  }

  useModalDeepLink(isApplePayLearnMoreModalOpen, apModalLearnMore)
  const removeCashAppPayEnabled = useFlag('enable-remove-cash-app-pay')

  const renderPaymentMethod = (): ReactNode => {
    if (!hasCreditCardData && !hasBankAccountData && !isApplePayAvailable && !hasCashAppPayData) {
      return (
        <>
          <Text size={textSize}>{t('account:tab:payment-methods:body:noPaymentMethods')}</Text>
          {isAchFlagEnabled && (
            <div className='mt-6'>
              <Text size={textSize}>{t('account:tab:payment-methods:body:noPaymentMethodsACH')}</Text>
            </div>
          )}
        </>
      )
    }

    return (
      <>
        <Grid>
          <Grid.Item sm={12} lg={6}>
            {isUSConsumer && <Text>{t('account:tab:payment-methods:body:secondaryPaymentDisclaimer')}</Text>}
            {hasCashAppPayData &&
              cashData?.cashAppPay.map((item) => {
                // Both the card and bank objects include a preferred method
                const isPreferredPaymentMethod = consumerPaymentMethod === PreferredPaymentMethod.CASH_APP_PAY
                return (
                  <React.Fragment key={item.id}>
                    <div className='py-4'>
                      <PaymentMethodRow
                        testNameSpace='cash-app-pay'
                        bankName={t('common:cashAppPay')}
                        number={item.cashtag}
                        type={PaymentMethodType.CASH_APP_PAY}
                        isPreferred={isPreferredPaymentMethod}
                        rightAdornment={
                          isPreferredPaymentMethod ? null : (
                            <PaymentMenu
                              paymentMethod={item}
                              showOptionToRemove={removeCashAppPayEnabled && numOfCards > 0}
                              showRemoveModal={handleShowRemoveModal}
                              showPreferredModal={handleShowPreferredModal}
                              setSelectedPaymentMethod={setSelectedPaymentMethod}
                              numOfCardsOnAccount={numOfCards}
                            />
                          )
                        }
                      />
                    </div>
                    <Divider className={styles.divider} kind='Hairline' margin='Half' />
                  </React.Fragment>
                )
              })}
            {hasCreditCardData &&
              cardData?.creditcards.map((item) => {
                // Both the card and bank objects include a preferred method
                const isPreferredPaymentMethod = item.preferred && consumerPaymentMethod === PreferredPaymentMethod.CREDIT_CARD
                const hasBillingAddress = item.billingAddress !== undefined
                const hidePaymentMenu = isPreferredPaymentMethod && !isReportCardCancellationCtaVisible && !hasBillingAddress
                const showAmexError = isAmexSoftCloseEnabled && item.cardBrand === 'AMEX'
                const shouldBlockChase = isChaseCreditCardBlocked(item, true)

                return (
                  <React.Fragment key={item.id}>
                    <div className='py-4'>
                      <PaymentMethodRow
                        testNameSpace='card'
                        key={item.id}
                        number={maskCreditCardNumber(item.truncatedNumber ?? item.maskedPan)}
                        expiryDate={item.expiryDate}
                        isPreferred={isPreferredPaymentMethod}
                        cardType={CardBrand[item.cardBrand]}
                        textColorFire={showAmexError}
                        rightAdornment={
                          hidePaymentMenu ? null : (
                            <PaymentMenu
                              paymentMethod={item}
                              showOptionToRemove={!isPreferredPaymentMethod && numOfCards > 1}
                              showRemoveModal={handleShowRemoveModal}
                              setSelectedPaymentMethod={setSelectedPaymentMethod}
                              showPreferredModal={handleShowPreferredModal}
                              showExistingOrderModal={handleShowExistingOrderModal}
                              showOptionToSetAsPreferred={!isPreferredPaymentMethod && !showAmexError && !shouldBlockChase}
                              numOfCardsOnAccount={numOfCards}
                              showOneCardModal={openOneCardModal}
                              showCardBillingAddressModal={openCardBillingAddressModal}
                              showReportCardCancellation={isReportCardCancellationCtaVisible}
                              showReportCardCancellationModal={openReportCardCancellationModal}
                              hasBillingAddress={hasBillingAddress}
                            />
                          )
                        }
                      />
                      {item.cardExpiryStatus === CardExpiryStatus.EXPIRING_SOON && (
                        <div className={'pt-4'}>
                          <Text color={'Fire'} size={textSize} testNameSpace={'expiring-soon'}>
                            {t('account:tab:payment-methods:body:expiringSoon')}
                          </Text>
                        </div>
                      )}
                    </div>
                    <Divider className={styles.divider} kind='Hairline' margin='Half' />
                  </React.Fragment>
                )
              })}
            {hasBankAccountData &&
              bankData.accounts.map((item) => {
                const isPaymentMethodUnlinked = item.status === AchBankAccountStatuses.UNLINKED
                // Both the card and bank objects include a preferred method
                const isPreferredPaymentMethod = item.preferred && consumerPaymentMethod === PreferredPaymentMethod.ACH_BANK_ACCOUNT
                const isLinkedPreferredPaymentMethod = isPreferredPaymentMethod && !isPaymentMethodUnlinked
                // The consumer can set the bank account as preferred when bank account is LINKED.
                const showOptionToSetAsPreferred = item.status === AchBankAccountStatuses.LINKED
                return (
                  <React.Fragment key={item.id}>
                    <div className='py-4'>
                      <PaymentMethodRow
                        testNameSpace='bank-account'
                        number={maskCreditCardNumber(item.numberMask)}
                        bankName={item.issuingBank}
                        type={PaymentMethodType.ACH_BANK_ACCOUNT}
                        isPreferred={isPreferredPaymentMethod}
                        rightAdornment={
                          isLinkedPreferredPaymentMethod ? null : (
                            <PaymentMenu
                              paymentMethod={item}
                              showOptionToRemove={!isPreferredPaymentMethod}
                              showPreferredModal={isPreferredPaymentMethod ? undefined : handleShowPreferredModal}
                              showRemoveModal={isPreferredPaymentMethod ? undefined : handleShowRemoveModal}
                              setSelectedPaymentMethod={setSelectedPaymentMethod}
                              showExistingOrderModal={handleShowExistingOrderModal}
                              showOptionToSetAsPreferred={!isPreferredPaymentMethod && showOptionToSetAsPreferred}
                              numOfCardsOnAccount={numOfCards}
                              isBankAccountUnlinked={isPaymentMethodUnlinked}
                              showRelinkModal={() => {
                                clickRelinkItem(item)
                              }}
                            />
                          )
                        }
                        textColorFire={item.status === AchBankAccountStatuses.UNLINKED}
                      />
                    </div>
                    <Divider className={styles.divider} kind='Hairline' margin='Half' />
                  </React.Fragment>
                )
              })}
            {!isApplePayDisabled && (
              <div className='py-4'>
                <PaymentMethodRow
                  isPreferred={isApplePayPreferred}
                  type={PaymentMethodType.APPLE_PAY}
                  disabled={!isApplePayAvailable}
                  bodyComponent={
                    <div className='flex flex-row items-center'>
                      <div className={clsx(!isApplePayAvailable && 'opacity-50', 'ml-2', 'md:ml-4')}>
                        <Text size={textSize}>Apple Pay</Text>
                      </div>
                      <div className='ml-2 md:ml-4'>
                        <Link onClick={() => openApplePayLearnMoreModal()}>Learn More</Link>
                      </div>
                    </div>
                  }
                  rightAdornment={
                    isApplePayPreferred ? null : (
                      <PaymentMenu
                        disabled={!isApplePayAvailable}
                        paymentMethod={PaymentMethodType.APPLE_PAY}
                        showPreferredModal={handleShowPreferredModal}
                        setSelectedPaymentMethod={setSelectedPaymentMethod}
                        numOfCardsOnAccount={numOfCards}
                      />
                    )
                  }
                  testNameSpace='payment-method-apple-pay'
                />
              </div>
            )}
          </Grid.Item>
        </Grid>
      </>
    )
  }

  const handleSetPaymentMethodPreferred = (): void => {
    if (selectedPaymentMethod === PaymentMethodType.APPLE_PAY) {
      handlePreferredApplePay(handleClosePreferredModal)
    }
    if (isBankAccount(selectedPaymentMethod)) {
      handlePreferredBank(handleClosePreferredModal)
    }
    if (isCard(selectedPaymentMethod)) {
      handlePreferredCard(handleClosePreferredModal)
    }
    if (isCashAppPay(selectedPaymentMethod)) {
      handlePreferredCashAppPay(handleClosePreferredModal)
    }
  }

  const handleRemovePaymentMethod = (): void => {
    if (isCard(selectedPaymentMethod)) {
      handleRemoveCard(handleCloseRemoveModal)
    }
    if (isBankAccount(selectedPaymentMethod)) {
      handleRemoveBank(handleCloseRemoveModal)
    }
    if (isCashAppPay(selectedPaymentMethod)) {
      handleRemoveCashAppPay(handleCloseRemoveModal)
    }
  }

  const paymentDataLoading = (rowNumber: number = 4): JSX.Element => {
    return (
      <>
        {[...new Array(rowNumber)].map((_, i) => (
          <div key={`payment-method-loading-${i}`} className='relative my-4 h-14 w-1/2' data-testid={`skeleton-${i}-div`}>
            <Skeleton />
          </div>
        ))}
      </>
    )
  }

  const isPaymentMethodsLoading = isLoadingCardData || isFetchingCardData || isLoadingBankData || isFetchingBankData || isLoadingCashData || isFetchingCashData
  const isPaymentMethodBank = isBankAccount(selectedPaymentMethod)

  return (
    <>
      <Heading bold size={subheadingSize} testNameSpace='body-title'>
        {t('account:tab:payment-methods:title')}
      </Heading>
      {isBankError || isLoadingCardError ? (
        <ErrorDialog onClick={handleDataRetrievalError} />
      ) : (
        <div className={isDesktop ? styles.detailContainer : undefined}>
          {isPaymentMethodsLoading ? (
            paymentDataLoading()
          ) : (
            <>
              {renderPaymentMethod()}

              <div className={buttonContainerStyle}>
                <Button.Primary
                  size='Large'
                  padding={!isDesktop ? 'Fluid' : 'Fixed'}
                  onClick={isAchEnabled ? openAchFlowModal : openAddCardModal}
                  testNameSpace='add-payment-method'
                >
                  {t('account:tab:payment-methods:body:cta')}
                </Button.Primary>
              </div>
              <ActionModal
                handleClick={handleRemovePaymentMethod}
                onClose={handleCloseRemoveModal}
                onBackdropClick={handleCloseRemoveModal}
                onEscapeKeyDown={handleCloseRemoveModal}
                show={showRemoveModal}
                title={t('account:tab:payment-methods:removeModal:title')}
                content={t('account:tab:payment-methods:removeModal:body')}
                ctaLabel={t('account:tab:payment-methods:body:paymentMethodCta:remove')}
                selectedMethod={selectedPaymentMethod}
              />
              <ActionModal
                handleClick={handleCloseExistingOrderModal}
                onClose={handleCloseExistingOrderModal}
                onBackdropClick={handleCloseExistingOrderModal}
                onEscapeKeyDown={handleCloseExistingOrderModal}
                show={showExistingOrderModal}
                title={t('account:tab:payment-methods:existingOrderModal:title')}
                content={existingOrderContent}
                ctaLabel={t('account:tab:payment-methods:existingOrderModal:cta')}
                selectedMethod={selectedPaymentMethod}
              />
              <ActionModal
                handleClick={handleSetPaymentMethodPreferred}
                onClose={handleClosePreferredModal}
                onBackdropClick={handleClosePreferredModal}
                onEscapeKeyDown={handleClosePreferredModal}
                show={showPreferredModal}
                title={isPaymentMethodBank ? t('account:tab:payment-methods:preferredModal:title-ach') : t('account:tab:payment-methods:preferredModal:title')}
                content={
                  <>
                    {t('account:tab:payment-methods:preferredModal:body-1')} <br /> <br />
                    {isCard(selectedPaymentMethod) && (
                      <>
                        <Checkbox
                          label={t('account:tab:payment-methods:preferredModal:includingAgreements')}
                          defaultChecked={true}
                          name='includingAgreements'
                          id='includingAgreementsCbx'
                          onClick={() => setIncludingAgreements(!includingAgreements)}
                          testNameSpace='includingAgreements-cbx'
                        />
                        <br />
                      </>
                    )}
                    {t('account:tab:payment-methods:preferredModal:body-2')}
                    {isPaymentMethodBank && (
                      <>
                        <br /> <br />
                        <Text size='S'>
                          <Trans
                            i18nKey={'account:tab:payment-methods:preferredModal:body-ach'}
                            // @ts-expect-error TODO: fix type error
                            components={{
                              numberMask: selectedPaymentMethod.numberMask,
                              paymentAuthorisationLink: (
                                <Link href={t('account:tab:payment-methods:preferredModal:ach-payment-authorisation-link')} kind='Underline' target='_blank' />
                              ),
                            }}
                          />
                        </Text>
                      </>
                    )}
                  </>
                }
                ctaLabel={isPaymentMethodBank ? t('account:tab:payment-methods:preferredModal:cta-ach') : t('account:tab:payment-methods:preferredModal:cta')}
                selectedMethod={selectedPaymentMethod}
              />
              <ActionModal
                handleClick={handleCloseModalOpenAddCard}
                onClose={closeOneCardModal}
                onBackdropClick={closeOneCardModal}
                onEscapeKeyDown={closeOneCardModal}
                show={isOneCardModalOpen}
                title={t('account:tab:payment-methods:oneCardModal:title')}
                content={t('account:tab:payment-methods:oneCardModal:body')}
                ctaLabel={t('account:tab:payment-methods:oneCardModal:cta')}
              />
              <AchFlowModal
                show={isAchFlowModalOpen}
                closeModal={closeAchFlowModal}
                initialStep={achFlowInitialStep}
                bankAccountToBeRelinked={bankAccountToBeRelinked}
                onSuccess={() => setIsAchSuccess(true)}
                achFlowEntryDeepLink={`${Route.ACCOUNT__PAYMENT_METHODS__ADDBANKACCOUNTMODAL}`}
              />
              <AddCardModal show={isAddCardModalOpen} onClose={closeAddCardModal} />
              <RelinkModal show={isRelinkModalOpen} onClose={closeRelinkModal} bankAccount={bankAccountToBeRelinked} onCallback={openAchFlowModalPlaidStep} />
              <ApplePayLearnMoreModal onClose={closeApplePayLearnMoreModal} show={isApplePayLearnMoreModalOpen} browserSupported={isApplePayAvailable} />
              {isCard(selectedPaymentMethod) && (
                <BillingAddressModal paymentMethod={selectedPaymentMethod} show={isCardBillingAddressModalOpen} handleClose={closeCardBillingAddressModal} />
              )}
              <ReportCardCancellationModal
                isModalOpen={isReportCardCancellationModalOpen}
                closeModal={closeReportCardCancellationModal}
                defaultPaymentMethod={selectedPaymentMethod as CreditCard}
                refetchCard={refetchCard}
              />
            </>
          )}
        </div>
      )}
    </>
  )
}
